<template>
	<div class="profile-container">
		<div class="row g-3">
			<!-- Profile Sidebar -->
			<div class="col-lg-3 d-none d-lg-block">
				<div class="profile-sidebar sticky-top">
					<div class="profile-cover d-flex align-items-end justify-content-center">
						<div class="position-relative" style="transform: translateY(45px);">
							<img class="img-fluid profile-pic" :src="client.steam_avatar || '/images/icons/p5.png'"
								alt="profile"
								:style="client.avatar_border_color ? { 'background-color': client.avatar_border_color } : {}">
							<span v-if="client.is_premium" class="badge-premium">VIP</span>
						</div>
					</div>
					<div class="profile-name">
						<h5 class="user-name" :style="client.nickname_color ? { color: client.nickname_color } : {}">{{
							client.name }}</h5>
						<h6>{{ client.email || 'Email не указан' }}</h6>
					</div>
					<ul class="profile-list">
						<li v-for="(tab, key) in profileTabs" :key="key" :class="{ active: activeTab === key }">
							<i :class="tab.icon"></i>
							<a href="#" @click.prevent="setActiveTab(key)">{{ tab.title }}</a>
						</li>
						<li>
							<i class="ri-logout-box-r-line"></i>
							<a href="#" @click.prevent="logout">Выйти</a>
						</li>
					</ul>
				</div>
			</div>

			<!-- Profile Content -->
			<div class="col col-lg-9">
				<!-- Profile Info Tab -->
				<ProfileInfo v-if="activeTab === 'profile'" :client="client" :telegramBotName="telegramBotName"
					:profileTabs="profileTabs" :activeTab="activeTab"
					@update-client="updateClient" @set-tab="setActiveTab" @logout="logout" />

				<!-- Trading Tab -->
				<ProfileTrading v-else-if="activeTab === 'trading'" :client="client" />

				<!-- Inventory Tab -->
				<ProfileInventory v-else-if="activeTab === 'inventory'" :client="client"
					@switch-to-trading="setActiveTab('trading')" />

				<!-- Favorites Tab -->
				<ProfileFavorites v-else-if="activeTab === 'favorites'" :client="client" />

				<!-- Orders Tab -->
				<ProfileOrders v-else-if="activeTab === 'orders'" :client="client" />

				<!-- Sales Tab -->
				<ProfileSales v-else-if="activeTab === 'sales'" :client="client" />

				<!-- Auctions Tab -->
				<ProfileAuctions v-else-if="activeTab === 'auctions'" :client="client" />

				<!-- Balance Tab -->
				<ProfileBalance v-else-if="activeTab === 'balance'" :client="client"
					:deposit-settings="depositSettings"
					:withdraw-settings="withdrawSettings"
					@update-client="updateClient" />

				<!-- Notifications Tab -->
				<ProfileNotifications v-else-if="activeTab === 'notifications'" :client="client"
					@update-client="updateClient" />

				<!-- Other Tabs Placeholder -->
				<div v-else class="text-center py-5">
					<h4>{{ getTabTitle(activeTab) }}</h4>
					<p class="text-muted">Эта функция будет реализована позже</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ProfileInventory from './Inventory.vue';
import ProfileInfo from './Info.vue';
import ProfileTrading from './Trading.vue';
import ProfileFavorites from './Favorites.vue';
import ProfileOrders from './Orders.vue';
import ProfileSales from './Sales.vue';
import ProfileAuctions from './Auctions.vue';
import ProfileBalance from './Balance.vue';
import ProfileNotifications from './Notifications.vue';
export default {
	name: 'ProfileLayout',
	components: {
		ProfileInventory,
		ProfileInfo,
		ProfileTrading,
		ProfileFavorites,
		ProfileOrders,
		ProfileSales,
		ProfileAuctions,
		ProfileBalance,
		ProfileNotifications
	},
	props: {
		initialClient: {
			type: Object,
			required: true
		},
		telegramBotName: {
			type: String,
			default: ''
		},
		depositSettings: {
			type: Object,
			default: () => ({
				minimum_amount: 100,
				maximum_amount: 50000,
				enabled: true
			})
		},
		withdrawSettings: {
			type: Object,
			default: () => ({ minimum_amount: 100 })
		}
	},

	data() {
		const profileTabs = window.profileTabs || {};
		const validTabs = Object.keys(profileTabs);

		// Получаем начальную вкладку
		const getInitialTab = () => {
			// Проверяем, что мы в браузере
			if (typeof window === 'undefined') {
				return 'profile';
			}

			// Проверяем hash в URL
			const hash = window.location.hash.substring(1);
			if (hash && validTabs.includes(hash)) {
				return hash;
			}

			// Проверяем localStorage
			try {
				const savedTab = localStorage.getItem('profile-active-tab');
				if (savedTab && validTabs.includes(savedTab)) {
					return savedTab;
				}
			} catch (e) {
				// localStorage недоступен
			}

			return 'profile';
		};

		return {
			client: { ...this.initialClient },
			activeTab: getInitialTab(),
			profileTabs: profileTabs,
			validTabs: validTabs
		}
	},

	methods: {
		setActiveTab(tab) {
			this.activeTab = tab;

			// Сохраняем активную вкладку в localStorage
			try {
				localStorage.setItem('profile-active-tab', tab);
			} catch (e) {
				// localStorage недоступен
			}

			// Обновляем hash в URL
			if (typeof window !== 'undefined') {
				window.history.replaceState(null, null, '#' + tab);
			}

			// Прокручиваем страницу к верху
			window.scrollTo({ top: 0, behavior: 'smooth' });

		},

		updateClient(updatedClient) {
			this.client = { ...this.client, ...updatedClient };
		},

		logout() {
			if (confirm('Вы уверены, что хотите выйти?')) {
				window.location.href = '/auth/logout';
			}
		},

		getTabTitle(tab) {
			return this.profileTabs[tab]?.title || 'Раздел';
		},

		handleHashChange() {
			// Обрабатываем изменение хэша в URL
			const hash = window.location.hash.substring(1);
			if (hash && this.validTabs.includes(hash)) {
				this.setActiveTab(hash);
			}
		}
	},

	mounted() {
		// Если hash отсутствует — ставим текущую активную вкладку в URL
		if (!window.location.hash) {
			window.history.replaceState(null, null, '#' + this.activeTab);
		}
		// Слушаем изменения хэша в URL
		window.addEventListener('hashchange', this.handleHashChange);
	},

	beforeUnmount() {
		// Убираем слушатель при уничтожении компонента
		window.removeEventListener('hashchange', this.handleHashChange);
	}
}
</script>